import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, Autocomplete } from '@mui/material';
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';

interface AddEntityPopupProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (data: { entityName: string; country: string }) => void;
    isMobile?: boolean;
}

interface CountryOption {
    value: string;
    label: string;
}

const AddEntityPopup: React.FC<AddEntityPopupProps> = ({ open, onClose, onConfirm, isMobile = false }) => {
    const [entityName, setEntityName] = useState('');
    const [country, setCountry] = useState<CountryOption | null>(null);
    const [errors, setErrors] = useState<{ entityName?: string; country?: string }>({});
    const [countryOptions, setCountryOptions] = useState<CountryOption[]>([]);

    // Fetch countries from API
    const [fetchCountries, setFetchCountries] = useApi();

    useEffect(() => {
        if (open) {
            const { url, method } = buildApiRequestConfig(routes.country.fetch);
            setFetchCountries({ url, method });
        }
    }, [open, setFetchCountries]);

    useEffect(() => {
        const { data, isLoading } = fetchCountries;
        if (data && !isLoading && Array.isArray(data)) {
            // Transform API response to autocomplete options
            const options: CountryOption[] = data.map((item: any) => ({
                value: String(item.id),
                label: item.country,
            }));
            setCountryOptions(options);
        }
    }, [fetchCountries]);

    const validate = () => {
        const newErrors: { entityName?: string; country?: string } = {};
        if (!entityName.trim()) {
            newErrors.entityName = 'Entity name is required';
        }
        if (!country) {
            newErrors.country = 'Country is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleConfirm = () => {
        if (validate()) {
            onConfirm({ entityName: entityName.trim(), country: country!.value });
            handleClose();
        }
    };

    const handleClose = () => {
        setEntityName('');
        setCountry(null);
        setErrors({});
        onClose();
    };

    const content = (
        <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 22, pb: 1 }}>Add New Company</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <Box mb={2}>
                    <Typography fontSize={14} color="var(--color-text-muted)" mb={0.5}>
                        Company Name <span style={{ color: '#F44336' }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter entity name"
                        value={entityName}
                        onChange={(e) => {
                            setEntityName(e.target.value);
                            if (errors.entityName) {
                                setErrors({ ...errors, entityName: undefined });
                            }
                        }}
                        error={!!errors.entityName}
                        helperText={errors.entityName}
                        sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    />
                </Box>

                <Box mb={2}>
                    <Typography fontSize={14} color="var(--color-text-muted)" mb={0.5}>
                        Country of Operation<span style={{ color: '#F44336' }}>*</span>
                    </Typography>
                    <Autocomplete
                        options={countryOptions}
                        value={country}
                        onChange={(event, newValue) => {
                            setCountry(newValue);
                            if (errors.country) {
                                setErrors({ ...errors, country: undefined });
                            }
                        }}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value?.value}
                        renderInput={(params) => <TextField {...params} placeholder="Select a country" />}
                        size='small'
                        sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    />
                    {errors.country && (
                        <Typography fontSize={12} color="#F44336" mt={0.5}>
                            {errors.country}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Button
                    onClick={handleClose}
                    variant="contained"
                    sx={{
                        background: 'var(--color-container-background)',
                        color: 'var(--color-text-muted)',
                        boxShadow: 'none',
                        borderRadius: 2,
                        fontWeight: 600,
                        width: 120,
                        '&:hover': { background: 'var(--color-container-background)' },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    sx={{
                        background: 'var(--color-primary)',
                        color: '#fff',
                        boxShadow: 'none',
                        borderRadius: 2,
                        fontWeight: 600,
                        width: 120,
                        '&:hover': { background: '#009b83' },
                    }}
                >
                    Add Company
                </Button>
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={handleClose} fitContent>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
            {content}
        </Dialog>
    );
};

export default AddEntityPopup;
